import React, { useRef, useState } from 'react';
import clsx from 'clsx';
import { PlateElement, useEditorRef, useReadOnly } from 'platejs/react';
import { ResizeHandle } from '@platejs/resizable';
import { resizeColumn } from '@platejs/layout';
import styles from './ColumnElement.module.scss';

// Minimum column width in percent. Smaller than this and the column
// becomes unreadable.
const MIN_COLUMN_PCT = 10;

// Single column inside a column_group. Width is a percentage string set
// on the node and surfaced as inline `flex-basis` so the parent flex row
// honours the requested ratio. A right-edge ResizeHandle is rendered on
// every column EXCEPT the last (the last has no sibling to redistribute
// width into).
export const ColumnElement = React.memo(function ColumnElement(props: any) {
    const editor = useEditorRef();
    const readOnly = useReadOnly();
    const innerRef = useRef<HTMLDivElement | null>(null);
    const storedWidth: string = (props.element?.width as string) || '50%';
    const columnId: string | undefined = props.element?.id;
    // Live drag preview width. Non-null while the user is dragging the right-edge
    // handle so the column visually resizes in real time; nulled on commit/release.
    const [tempWidth, setTempWidth] = useState<string | null>(null);
    const width = tempWidth ?? storedWidth;

    // Resolve our path + the parent group so we can detect "is this the
    // last column" and target setNodes at the parent group.
    let path: any = null;
    let groupPath: any = null;
    let isLastColumn = false;
    try {
        const ed: any = editor;
        path = ed?.api?.findPath?.(props.element);
        if (Array.isArray(path) && path.length > 0) {
            groupPath = path.slice(0, -1);
            const groupEntry = ed?.api?.node?.(groupPath);
            const siblings = groupEntry?.[0]?.children ?? [];
            const myIndex = path[path.length - 1];
            isLastColumn = myIndex === siblings.length - 1;
        }
    } catch {
        /* defensive */
    }

    const onResize = (event: any) => {
        try {
            const ed: any = editor;
            const groupEntry = ed?.api?.node?.(groupPath);
            const groupNode = groupEntry?.[0];
            if (!groupNode) return;
            // The column GROUP is two levels up from innerRef:
            //   innerRef.current   → .columnInner
            //   .parentElement     → .column (PlateElement wrapper)
            //   .parentElement     → .columnGroup (flex row holding all columns)
            // Earlier code stopped one level too shallow and used the column's
            // own width as the container, making the resize feel ~2× too
            // sensitive — small drags would snap to the clamp limits and the
            // bar looked "stuck".
            const groupEl: HTMLElement | null = innerRef.current?.parentElement?.parentElement ?? null;
            const containerPx = groupEl?.offsetWidth ?? 0;
            if (containerPx <= 0) return;
            const startPct = parseFloat(storedWidth) || 100 / Math.max(groupNode.children?.length || 1, 1);
            // @platejs/resizable's ResizeEvent.delta is a SCALAR (px along
            // the handle's direction), NOT a {x,y} object. The original code
            // read .delta.x → undefined → 0 every tick, so the bar felt
            // interactive but never actually resized.
            const deltaPx: number = typeof event?.delta === 'number' ? event.delta : 0;
            const deltaPct = deltaPx * (100 / containerPx);
            const newPct = Math.max(MIN_COLUMN_PCT, Math.min(100 - MIN_COLUMN_PCT, startPct + deltaPct));

            if (!event?.finished) {
                // Live preview during drag — purely visual; the editor node
                // doesn't change until release so we get a single undo step.
                setTempWidth(`${newPct}%`);
                return;
            }

            // Commit on release.
            setTempWidth(null);
            const id = columnId;
            if (!id) {
                // resizeColumn keys off `id`. Without one we cannot commit;
                // the visual preview snaps back to the stored width. Newly
                // inserted columns are auto-assigned IDs by the ColumnItemPlugin
                // normalizer.
                return;
            }
            // resizeColumn mutates the group in place AND returns it.
            // Clone before mutating so we don't mutate the immutable editor
            // value tree.
            const clone = JSON.parse(JSON.stringify(groupNode));
            (resizeColumn as any)(clone, id, newPct);
            // Apply the recomputed widths to each child via setNodes.
            (clone.children as any[]).forEach((col: any, i: number) => {
                if (typeof col.width === 'string') {
                    ed.tf.setNodes({ width: col.width }, { at: [...groupPath, i] });
                }
            });
        } catch {
            // Reset preview on any error so the column doesn't get stuck
            // showing a stale drag width.
            setTempWidth(null);
        }
    };

    return (
        <PlateElement
            {...props}
            as="div"
            className={clsx(props.className, styles.column)}
            style={{
                ...(props.style ?? {}),
                flex: `0 0 ${width}`,
                width,
            }}
        >
            <div ref={innerRef} className={styles.columnInner}>
                {props.children}
            </div>
            {!readOnly && !isLastColumn && (
                <ResizeHandle className={styles.colResizeHandle} options={{ direction: 'right', onResize }} contentEditable={false} />
            )}
        </PlateElement>
    );
});
